import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trophy } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RankTable, useMyName } from "@/pages/Rankings";

/**
 * CompetitionResults — public ranking for GRANDE FINAL / competition games
 * (status="competition"), played WITH credentials. Distinct from the
 * generic /resultados page (which hosts VS TEMPO / VS ROBOT / VS HUMANO).
 * Picks a competition game + level/escalão and shows the credential ranking.
 */
export default function CompetitionResults() {
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  const myName = useMyName();

  const initialGame = searchParams.get("game") || "";
  const initialLevel = searchParams.get("level") || "all";
  const initialTier = searchParams.get("tier") || "all";

  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState(initialGame);
  const [level, setLevel] = useState(initialLevel);
  const [tierId, setTierId] = useState(initialTier);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/results/public/games", { params: { lang, status: "competition" } }).then((r) => {
      const data = r.data || [];
      setGames(data);
      if (data.length) {
        const wanted = initialGame && data.find((g) => g.id === initialGame);
        setGameId(wanted ? initialGame : data[0].id);
      } else {
        setGameId("");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const currentGame = useMemo(() => games.find((g) => g.id === gameId), [games, gameId]);
  const useTiers = currentGame?.level_system === "tiers" && Array.isArray(currentGame?.tiers);

  useEffect(() => {
    if (!gameId || !currentGame) { setRows([]); return; }
    const base = `/rankings/competition/${gameId}`;
    let url = base;
    if (useTiers && tierId !== "all") {
      url = `${base}?tier_id=${encodeURIComponent(tierId)}`;
    } else if (!useTiers && level !== "all") {
      url = `${base}?level=${parseInt(level, 10)}`;
    }
    api.get(url).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [gameId, currentGame, level, tierId, useTiers]);

  const topN = currentGame?.top_n || 10;
  const filterAndLimit = (data) => (data || []).slice(0, level === "all" && tierId === "all" ? data.length : topN);

  const buildColumns = () => {
    const cols = ["ranking"];
    if (currentGame?.show_name) cols.push("name");
    cols.push("flag");
    cols.push("school");
    if (level === "all" && tierId === "all") cols.push("level");
    if (currentGame?.show_best_time) cols.push("best_time");
    cols.push("share");
    return cols;
  };

  const subtitle = currentGame?.[`subtitle_${lang}`] || currentGame?.subtitle_pt || "";

  return (
    <SiteChrome>
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-[Fredoka] font-semibold text-white mb-2 flex items-center gap-3" data-testid="comp-results-title">
          <Trophy className="w-8 h-8 text-amber-300" />
          {t("comp_results_title")}
        </h1>
        {subtitle ? (
          <p className="text-white/80 text-sm md:text-base font-[Nunito] mb-6 max-w-2xl" data-testid="comp-results-subtitle">
            {subtitle}
          </p>
        ) : (
          <p className="text-white/70 text-sm font-[Nunito] mb-6 max-w-2xl" data-testid="comp-results-intro">
            {t("comp_results_intro")}
          </p>
        )}

        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex-1 min-w-[260px] max-w-sm">
            <label className="text-xs font-bold text-white/70 uppercase">{t("col_game")}</label>
            <Select value={gameId} onValueChange={setGameId}>
              <SelectTrigger data-testid="comp-rank-game"><SelectValue placeholder={t("select_game")} /></SelectTrigger>
              <SelectContent>
                {games.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <label className="text-xs font-bold text-white/70 uppercase">
              {useTiers ? t("col_level_or_tier") : t("col_level")}
            </label>
            <Select
              value={useTiers ? tierId : level}
              onValueChange={(v) => { if (useTiers) setTierId(v); else setLevel(v); }}
            >
              <SelectTrigger data-testid="comp-rank-level"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{useTiers ? t("all_tiers") : t("all_levels")}</SelectItem>
                {useTiers
                  ? currentGame.tiers.map((tier) => (
                      <SelectItem key={tier.id} value={tier.id}>{tier.name}</SelectItem>
                    ))
                  : (currentGame?.format === "quiz" ? [1, 2, 3, 4] : [1, 2, 3, 4, 5]).map((l) => (
                      <SelectItem key={l} value={String(l)}>{t("level")} {l}</SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!games.length ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500" data-testid="comp-results-empty">
            {lang === "en" ? "No competition games with public results." : lang === "es" ? "No hay juegos de competición con resultados públicos." : "Sem jogos de competição com resultados públicos."}
          </div>
        ) : (
          <RankTable rows={filterAndLimit(rows)} columns={buildColumns()} game={currentGame} t={t} lang={lang} myName={myName} />
        )}
      </div>
    </SiteChrome>
  );
}
